import {
  CheckoutFlowStep,
  useBanksRequest,
  useCancelPaymentRequest,
  useCheckoutPayment,
  useEventTracker,
  useSessionId,
  useUnloadBeacon
} from 'mmfintech-checkout-commons'
import type { CheckoutMethodField, CountryChoice, SupportedBank } from 'mmfintech-checkout-commons/types'
import {
  formatMoney,
  isEmptyString,
  isValidArray,
  isValidObject,
  isValidString,
  parseJson,
  tr,
  translateCountryName
} from 'mmfintech-commons'
import { Button, ErrorDisplay, Input } from 'mmfintech-portal-commons'
import { useCallback, useEffect, useState } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import { Link, useHistory } from 'react-router-dom'
import { CheckoutContainer, CheckoutContent, CustomPreloader, LabelValue, SelectMethod } from '../../components'
import { DepositResult } from '../DepositResult'
import styles from './Checkout.module.scss'
import { CancelContainer, FooterContainer, PaymentContainer } from './Checkout.styled'
import { MerchantPane } from './MerchantPane'

const Checkout = () => {
  const sessionId = useSessionId()

  const {
    logo,
    checkoutCountries,
    checkoutPay,
    checkoutPayError,
    checkoutPayFetching,
    session,
    sessionError,
    sessionFetching
  } = useSelector(
    ({
      checkout: {
        logo,
        checkoutCountries,
        checkoutPay,
        checkoutPayError,
        checkoutPayFetching,
        session,
        sessionError,
        sessionFetching
      }
    }) => ({
      logo,
      checkoutCountries,
      checkoutPay,
      checkoutPayError,
      checkoutPayFetching,
      session,
      sessionError,
      sessionFetching
    }),
    shallowEqual
  )

  const [triggered, setTriggered] = useState(false)

  const history = useHistory()
  useUnloadBeacon(sessionId)

  const { trackEvent } = useEventTracker()
  const { cancelPayment } = useCancelPaymentRequest()

  const {
    step,
    // setStep,
    fieldValues,
    status,
    countryCode,
    setCountryCode,
    hasOnlyOneCountry,
    paymentMethod,
    setPaymentMethod,
    selectedPaymentOption,
    handleStartOver,
    handlePay,
    initialized
  } = useCheckoutPayment({
    initialStep: CheckoutFlowStep.SELECT_METHOD,
    preselectSingleMethod: true
  })

  // const { paymentOptions } = usePaymentOptions()
  const { banks, banksCount } = useBanksRequest()

  // const [filteredFields, setFilteredFields] = useState(fields)

  // useEffect(() => {
  //   if (!isValidArray(fields)) {
  //     setFilteredFields(fields)
  //   }
  //
  //   if (isValidArray(paymentOptions) && isValidArray(fields)) {
  //     const data = {}
  //     const selectedPaymentMethodFields = paymentOptions.find(
  //       (option: any) => option.paymentMethod === paymentMethod
  //     )?.fields
  //     setFilteredFields(selectedPaymentMethodFields)
  //     isValidArray(selectedPaymentMethodFields) &&
  //       selectedPaymentMethodFields.forEach((item: CheckoutMethodField) => {
  //         if (item.name !== 'bankChoiceId') {
  //           const { name, prefilledValue: value, required } = item
  //           data[name] = { value, required, ...prepareValidations(item) }
  //         }
  //       })
  //   }
  // }, [formValues.getValue('bankChoiceId'), paymentOptions, fields])

  const handleCancelClick = () => {
    if (sessionId) {
      cancelPayment(sessionId)
    }
    trackEvent(sessionId, 'cancelled_by_payer')
    history.push('/fail')
    return false
  }

  const handleBankSelected = (code: string) => {
    trackEvent(sessionId, 'bank_selected', code)
  }

  const prepareResponse = useCallback(() => {
    if (session?.sessionStatus?.value === 'IN_PROGRESS') {
      return {
        result: parseJson(session?.paymentResult, session?.paymentResult),
        resultType: session?.paymentResultType,
        processingAmount: session?.processingAmount,
        processingCurrency: session?.processingCurrency
      }
    }
    return checkoutPay
  }, [checkoutPay, session])

  const currencyOptions = isValidObject(selectedPaymentOption)
    ? (selectedPaymentOption.supportedCurrencies as string[])
    : []

  const validBankSelection = () =>
    !fieldValues.hasBankSelection ||
    (fieldValues.hasBankSelection && banksCount === 1 && isValidString(fieldValues.getValue('bankChoiceId')))

  const validFields = () => {
    if (selectedPaymentOption) {
      const { fields } = selectedPaymentOption
      if (isValidArray(fields)) {
        const find = fields.find((item: CheckoutMethodField) => {
          const { name, prefilledValue, displayIfPrefilled } = item
          return (!prefilledValue?.length || displayIfPrefilled) && name !== 'bankChoiceId'
        })
        return !find
      }
    }
    return true
  }

  useEffect(() => {
    if (
      initialized &&
      !triggered &&
      !checkoutPayFetching &&
      isValidString(paymentMethod) &&
      validBankSelection() &&
      validFields()
    ) {
      setTriggered(true)
    }
    // eslint-disable-next-line
  }, [initialized, fieldValues.getValue('bankChoiceId')])

  useEffect(() => {
    if (triggered) {
      handlePay()
    }
  }, [triggered])

  return (
    <CheckoutContainer>
      <CheckoutContent>
        {sessionFetching || !initialized ? (
          <PaymentContainer>
            <CustomPreloader />
          </PaymentContainer>
        ) : (
          <>
            {!sessionError && !checkoutPayError && <MerchantPane session={session} logo={logo} />}

            {step === CheckoutFlowStep.SELECT_METHOD ||
            step === CheckoutFlowStep.SELECT_BANK ||
            step === CheckoutFlowStep.ADDITIONAL_FIELDS ? (
              <>
                <PaymentContainer>
                  {status === 'IDLE' && (
                    <>
                      {hasOnlyOneCountry ? (
                        <div className='mb-3'>
                          <Input
                            type='select'
                            name='countryCode'
                            label={tr('CHECKOUT.PAYMENT.COUNTRY', 'Country')}
                            placeholder={tr('CHECKOUT.PAYMENT.COUNTRY_PLACEHOLDER', 'Select country')}
                            options={checkoutCountries?.map((country: CountryChoice) => ({
                              value: country.countryCode,
                              label: country.name
                            }))}
                            value={countryCode}
                            onChange={(_name: string, value: string) => setCountryCode(value)}
                            hideErrorLine
                            required
                          />
                        </div>
                      ) : (
                        <div className='mb-3'>
                          <Input
                            type='static'
                            label={tr('CHECKOUT.PAYMENT.COUNTRY', 'Country')}
                            value={translateCountryName(countryCode)}
                            hideErrorLine
                          />
                        </div>
                      )}

                      <SelectMethod
                        className='use-grid'
                        method={paymentMethod}
                        setMethod={setPaymentMethod}
                        errorVisible={!!countryCode}
                      />

                      {isValidArray(fieldValues.fields) ? (
                        <>
                          <div className='mt-2' />

                          {fieldValues.fields.map((field: CheckoutMethodField) => {
                            if (field.name === 'bankChoiceId') {
                              return (
                                <Input
                                  key={field.name}
                                  type='select'
                                  label={tr('CHECKOUT.PAYMENT.BANK', 'Bank')}
                                  {...fieldValues.registerInput(field.name, handleBankSelected)}
                                  options={
                                    Array.isArray(banks)
                                      ? banks.map((bank: SupportedBank) => {
                                          const { bankChoiceId, name } = bank
                                          return { value: bankChoiceId, label: name }
                                        })
                                      : []
                                  }
                                  hideErrorLine
                                />
                              )
                            }

                            if (field.name === 'currency') {
                              return (
                                <Input
                                  key={field.name}
                                  type='select'
                                  label={
                                    field.translationKey
                                      ? tr(field.translationKey, field.defaultLabel)
                                      : field.defaultLabel
                                  }
                                  {...fieldValues.registerInput(field.name)}
                                  options={currencyOptions?.map((currency: string) => {
                                    return { value: currency, label: currency }
                                  })}
                                  hideErrorLine
                                />
                              )
                            }

                            if (field.name === 'documentId' && paymentMethod === 'PIX') {
                              return (
                                <Input
                                  key={field.name}
                                  type={field.type || 'text'}
                                  label={
                                    field.translationKey
                                      ? tr(field.translationKey, field.defaultLabel)
                                      : field.defaultLabel
                                  }
                                  {...fieldValues.registerShort(field.name)}
                                  onChange={(name: string, value: string) =>
                                    fieldValues.setValue(name, value.replace(/[ ./-]/g, ''))
                                  }
                                  required={fieldValues.isFieldRequired(field)}
                                  hideErrorLine
                                  autoComplete='off'
                                />
                              )
                            }

                            return (
                              <Input
                                key={field.name}
                                type={field.type || 'text'}
                                label={
                                  field.translationKey
                                    ? tr(field.translationKey, field.defaultLabel)
                                    : field.defaultLabel
                                }
                                {...fieldValues.registerInput(field.name)}
                                hideErrorLine
                                autoComplete='off'
                              />
                            )
                          })}
                        </>
                      ) : null}
                    </>
                  )}

                  <ErrorDisplay error={[sessionError, checkoutPayError]} />

                  {Boolean(paymentMethod && fieldValues.getValue('currency')) && (
                    <LabelValue
                      label={tr('FRONTEND.BANKING.COMMON.AMOUNT_LABEL', 'Amount')}
                      value={formatMoney(
                        selectedPaymentOption?.currencyAmounts[fieldValues.getValue('currency')],
                        fieldValues.getValue('currency')
                      )}
                      dataTest='amount'
                      enableCopy={false}
                    />
                  )}

                  {status === 'IDLE' && (
                    <div className='buttons'>
                      <Button
                        type='button'
                        color='primary'
                        text={tr('CHECKOUT.PAYMENT.BUTTON_PAY', 'Pay')}
                        onClick={handlePay}
                        disabled={isEmptyString(paymentMethod)}
                        loading={checkoutPayFetching}
                        data-test='button-pay'
                      />
                    </div>
                  )}

                  {Boolean(session?.returnUrl) && (
                    <a className={styles['back-to-merchant-wrapper']} href={session?.returnUrl} target='_top'>
                      &larr; {tr('CHECKOUT.PAYMENT.BACK_TO_MERCHANT', 'Back to merchant')}
                    </a>
                  )}
                </PaymentContainer>

                {Boolean(session?.showCancelButton) && (
                  <CancelContainer>
                    <Link to='#' onClick={handleCancelClick}>
                      {tr('FRONTEND.BUTTONS.CANCEL', 'Cancel')}
                    </Link>
                  </CancelContainer>
                )}
              </>
            ) : null}
          </>
        )}

        {step === CheckoutFlowStep.DEPOSIT_RESULT && (
          <DepositResult response={prepareResponse()} sessionId={sessionId} onBack={handleStartOver} />
        )}

        <FooterContainer>
          Ecopayz Bank Limited is licensed and authorised by the Financial Services Unit, Ministry of Finance of the
          Commonwealth of Dominica, licensed as a banking institution under the International Banking Act, fully
          authorised to provide services to clients worldwide, under the prudential supervision of the Financial
          Services Unit. Ecopayz Bank Limited is registered in the Commonwealth of Dominica, Company Number C0163, with
          its registered address at 40 Kennedy Avenue, St. George, Roseau, Post Code: 00109-8000. LEI Code:
          894500ZVODRQC4QFLG56.
        </FooterContainer>
      </CheckoutContent>
    </CheckoutContainer>
  )
}

export default Checkout
